import React, { useEffect, useState } from 'react';
import { Loader, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';

interface AuditEvent {
  id: string;
  timestamp: string;
  eventType: string;
  userId?: string;
  userEmail?: string;
  details?: Record<string, any>;
}

interface Props {
  clientId: string;
  limit?: number;
}

const formatTs = (iso: string) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString('es-AR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const eventBadge: Record<string, string> = {
  sync_started: 'bg-blue-100 text-blue-700',
  sync_completed: 'bg-green-100 text-green-700',
  sync_failed: 'bg-red-100 text-red-700',
  status_changed: 'bg-amber-100 text-amber-700',
  type_changed: 'bg-purple-100 text-purple-700',
  created: 'bg-gray-100 text-gray-700',
};

const summarizeDetails = (eventType: string, details: any = {}): string => {
  if (!details || Object.keys(details).length === 0) return '—';
  switch (eventType) {
    case 'status_changed':
      return `${details.from?.active ? 'Activo' : 'Inactivo'} → ${details.to?.active ? 'Activo' : 'Inactivo'}${details.reason ? ` (${details.reason})` : ''}`;
    case 'type_changed':
      return `${details.fromType ?? '?'} → ${details.toType ?? '?'}`;
    case 'sync_completed':
      return `${details.rowsImported ?? 0} rows · ${details.duration_ms ?? 0}ms${details.tomaId ? ` · ${details.tomaId}` : ''}`;
    case 'sync_failed':
      return details.error || 'error';
    case 'sync_started':
      return details.tomaId || '—';
    default:
      return JSON.stringify(details);
  }
};

const AuditLogTable: React.FC<Props> = ({ clientId, limit = 100 }) => {
  const { t } = useTranslation();
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get(`/api/clients/${clientId}/audit-log`, { params: { limit } })
      .then((res) => {
        if (cancelled) return;
        const list = res.data?.events || res.data?.data || [];
        setEvents(Array.isArray(list) ? list : []);
      })
      .catch((err: any) => {
        if (cancelled) return;
        setError(err?.response?.data?.error || err?.message || 'Error');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId, limit]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8 text-gray-500">
        <Loader className="w-5 h-5 animate-spin mr-2" />
        {t('admin.audit.loading', 'Cargando historial...')}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
        <AlertCircle className="w-4 h-4" />
        {error}
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="text-center text-sm text-gray-500 p-6">
        {t('admin.audit.empty', 'Sin eventos registrados')}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 text-gray-600 text-left">
          <tr>
            <th className="px-3 py-2 font-medium">{t('admin.audit.timestamp', 'Fecha')}</th>
            <th className="px-3 py-2 font-medium">{t('admin.audit.event', 'Evento')}</th>
            <th className="px-3 py-2 font-medium">{t('admin.audit.user', 'Usuario')}</th>
            <th className="px-3 py-2 font-medium">{t('admin.audit.details', 'Detalles')}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {events.map((e) => (
            <tr key={e.id} className="hover:bg-gray-50">
              <td className="px-3 py-2 whitespace-nowrap text-gray-700">{formatTs(e.timestamp)}</td>
              <td className="px-3 py-2">
                <span
                  className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                    eventBadge[e.eventType] || 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {e.eventType}
                </span>
              </td>
              <td className="px-3 py-2 text-gray-600">{e.userEmail || e.userId || 'system'}</td>
              <td className="px-3 py-2 text-gray-600">{summarizeDetails(e.eventType, e.details)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default AuditLogTable;
